import React from 'react';
import { getImageURL } from '../../utils/imageUtils';
import { LocationIcon, BookmarkIcon } from '../Icons';

// Card-based list view of properties, shown in the same content-area slot
// the map normally occupies. Exists mainly so listings without coordinates
// (chalets have theirs redacted for non-staff viewers — see
// backend/controllers/landsController.js's CHALET_REDACTED_FIELDS) still
// have somewhere to show up, since they can never render as a map pin.
// Mirrors mobile's PropertyCard (mobile/src/components/PropertyCard.js)
// field-for-field for a consistent look between platforms.
const PropertiesList = ({ lands, onOpenDetail, formatPrice, t, language, savedLandIds = [], onToggleSave }) => {
  const isRTL = language === 'ar';

  if (!lands || lands.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white rounded-xl">
        <p className="text-gray-500 text-sm">{t('لا توجد عقارات مطابقة', 'No matching properties')}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto bg-white rounded-xl p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {lands.map((land) => {
          const title = [land.code_number, land.land_number].filter(Boolean).join(' | ') || t('عقار', 'Property');
          const phaseTag = (land.tags || []).find((tag) => tag.title && tag.title.includes('المرحلة'));
          const typeLabel = land.land_type_info
            ? (language === 'ar' ? land.land_type_info.display_name_ar : land.land_type_info.display_name_en)
            : null;
          const subtitle = [phaseTag?.title, typeLabel].filter(Boolean).join(' • ');
          const isSaved = savedLandIds.includes(land.id);

          return (
            <button
              key={land.id}
              onClick={() => onOpenDetail(land)}
              className={`text-${isRTL ? 'right' : 'left'} bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow`}
            >
              <div className="relative w-full h-40 bg-gray-100">
                {land.picture_url ? (
                  <img
                    src={getImageURL(land.picture_url)}
                    alt={title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-brand/5">
                    <LocationIcon className="w-8 h-8 text-brand/40" />
                  </div>
                )}

                {land.price ? (
                  <span className="absolute bottom-2 left-2 bg-brand text-white text-xs font-bold px-2 py-1 rounded-lg max-w-[75%] truncate">
                    {formatPrice(land.price)}
                  </span>
                ) : null}

                {onToggleSave && (
                  <span
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSave(land.id);
                    }}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 flex items-center justify-center"
                  >
                    <BookmarkIcon className="w-4 h-4 text-white" filled={isSaved} />
                  </span>
                )}
              </div>

              <div className="p-3">
                <p className="text-sm font-bold text-gray-900 truncate">{title}</p>
                {subtitle ? (
                  <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{subtitle}</p>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PropertiesList;
